import { memo, useCallback } from 'react';
import { CLIP_CUSTOMIZER_LAYOUT_TYPES } from '@goldcast/player-config/src/constants/layouts.constants';
import { LayoutDisableReason, LayoutType } from '@goldcast/player-config/src/types/asset-types';
import { LayoutPopupOption } from '../../../ClipCustomizer/Layout/constants';
import useMagicLayoutSelection from '../useMagicLayoutSelection';
import LayoutThumbnail from './LayoutThumbnail';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import TooltipV2 from '@/components/TooltipV2';
import { classnames } from '@/libs/utils';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';

function ClipCustomizerLayoutCard({
  magicLayoutOption,
  disabled,
  disabledReason
}: {
  magicLayoutOption: LayoutType;
  disabled: boolean;
  disabledReason: string;
}) {
  const { isActive, handleClick, isDialogOpen, dialogContent, handleConfirm, handleCancel } =
    useMagicLayoutSelection(magicLayoutOption);

  const optionDetails = CLIP_CUSTOMIZER_LAYOUT_TYPES.find(
    option => option.value === magicLayoutOption
  ) as LayoutPopupOption;

  const onCardClick = useCallback(() => {
    if (disabled) {
      if (disabledReason === LayoutDisableReason.UNMIGRATED_LIVE_EVENT_DOESNT_SUPPORT_SPEAKER_LAYOUTS) {
        EventBus.dispatch(CustomEvents.OpenEventMigrationPopup, { source: 'ClipCustomizerMagicLayout' });
      }
      return;
    }
    handleClick();
  }, [disabled, disabledReason, handleClick]);

  return (
    <>
      <TooltipV2
        tooltipElement={
          <button
            type="button"
            data-tracking-id={`customizer-magic-layout-card-${magicLayoutOption}`}
            onClick={onCardClick}
            className={classnames(
              'flex w-full flex-col items-start gap-[8px] rounded-[10px] bg-white p-[10px] pb-[8px] transition-colors',
              isActive ? 'border-2 border-black' : 'border-[1.5px] border-slate-200 hover:border-slate-300',
              disabled ? 'cursor-not-allowed opacity-30' : 'cursor-pointer'
            )}
          >
            <LayoutThumbnail layout={magicLayoutOption} />
            <p className="w-full text-center text-[12px] font-medium leading-[18px] text-black">
              {optionDetails.label}
            </p>
          </button>
        }
        tooltipContent={disabledReason || ''}
        tooltipId={`magic-layout-card-${magicLayoutOption}`}
        place="bottom"
        size="base"
        colorTheme="light"
        className="!px-3 !py-1.5 !text-sm"
        opacity={1}
        offset={12}
      />
      <ConfirmationDialog
        isOpen={isDialogOpen}
        title="Confirm Layout Change"
        content={dialogContent}
        confirmLabel="Continue"
        cancelLabel="Cancel"
        onClose={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
}

export default memo(ClipCustomizerLayoutCard);
